import { Button, Checkbox, Snack, Snackbar, Modal, StatusIconProtoProps } from '@nike/eds';
import { FC, useState } from 'react';
import { useI18nContext } from '../../context/language/use-i18n-context';
import ts from './i18n';
import './TAC.css';
import { AgreedToTerms } from '../../services/tc-service';
import React, { useEffect } from 'react';
import { useSponsorContext } from 'context/Sponsor';

export interface TACProps {
  tacTemplateName: string;
}

const TAC: FC<TACProps> = ({ tacTemplateName }) => {
  const { i18nString } = useI18nContext();
  const { setTacDetails, tacDetails, setSponsorDetails, sponsor } = useSponsorContext();
  const [state, setState] = useState({
    modelOpen: true,
    isChecked: false,
    showSnack: false,
    snackStatus: 'info' as StatusIconProtoProps['status'],
    isSingapore: tacTemplateName === 'TACSGEmp01.html',
  });

  const tcTemplate = tacTemplateName || 'TACUSAEmp01.html';

  const [templateExists, setTemplateExists] = useState<boolean>(false);

  useEffect(() => {
    checkTemplateExists();
  }, [tcTemplate, tacTemplateName]);

  async function checkTemplateExists() {
    try {
      const response = await fetch('/manifest.json');
      const manifest = await response.json();
      const templates: string[] = manifest.tcTemplates || [];
      setTemplateExists(templates.includes(tcTemplate));
    } catch (error) {
      setTemplateExists(false);
    }
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    setState(prevState => ({ ...prevState, isChecked: event.target.checked }));
  }

  function handleAgree() {
    if (!state.isChecked) {
      setState(prevState => ({ ...prevState, snackStatus: 'error', showSnack: true }));
      return;
    }

    AgreedToTerms()
      .then(() => {
        // Close the modal after accepting the terms and condition
        setTacDetails({ tacTemplateName: '', enabletac: false });
        setSponsorDetails({ ...sponsor, agreedToTerms: new Date() });
        setState(prevState => ({ ...prevState, modelOpen: false }));
      })
      .catch(() => {
        setState(prevState => ({ ...prevState, snackStatus: 'error', showSnack: true }));
      });
  }

  function Header() {
    return (
      <h1 className="modal__header" data-testid="tnc">
        {i18nString(ts.TAC_TERMS_AND_CONDITIONS)}
      </h1>
    );
  }

  function Footer() {
    return (
      <>
        <Checkbox
          className="tac__footer"
          label={i18nString(ts.TAC_AGREE_TO_TAC)}
          checked={state.isChecked}
          id="tac__checkbox"
          onChange={handleCheckboxChange}
          data-testid="tac-checkbox"
        />
        <p className="tac__footer" data-testid="tac-footer">
          {i18nString(ts.TAC_BY_CLICKING)}
        </p>
        <br />
        <Button id="tac__button" onClick={handleAgree} data-testid="tac-button">
          {i18nString(ts.TAC_ACCEPT_BUTTON)}
        </Button>
      </>
    );
  }

  return (
    <Modal
      isOpen={state.modelOpen}
      hideFade
      headerSlot={<Header />}
      onDismiss={() => {
        //close the modal only if the user has not accepted the terms and conditions
      }}
      footerSlot={<Footer />}
      id="my-modal-id"
      data-testid="tac-modal"
    >
      {!state.isSingapore && (
        <>
          <p id="tac__employee-discount" data-testid="tac-employee-discount">
            {i18nString(ts.TAC_TERMS_AND_CONDITIONS)}
          </p>
          <br />
          <p className="tac__content" data-testid="tac-content">
            {i18nString(ts.TAC_PLEASE_READ)}
          </p>
          <br />
        </>
      )}

      {state.isSingapore && (
        <>
          <p id="tac__employee-discount" data-testid="tac-employee-discount">
            {i18nString(ts.TAC_EMPLOYEE_DISCOUNT)}
          </p>
          <br />
          <div className="tac__content" data-testid="tac-content">
            <p data-testid="tac-post-to">{i18nString(ts.TAC_POST_TO)}</p>
            <p data-testid="tac-discount">{i18nString(ts.TAC_DISCOUNT_LOCATION)}</p>
            <p data-testid="tac-audience">{i18nString(ts.TAC_AUDIENCE)}</p>
          </div>
          <br />
        </>
      )}

      {templateExists ? (
        <iframe src={'/tc/' + tcTemplate} className="tac__scroll-window" data-testid="tac-iframe" />
      ) : (
        <iframe src={'/tc/NotFound.html'} className="tac__scroll-window" data-testid="tac-iframe" />
      )}

      <Snackbar>
        {state.showSnack && (
          <Snack
            id="1"
            status={state.snackStatus}
            onDismiss={() => setState(prevState => ({ ...prevState, showSnack: false }))}
            data-testid="tac-snack"
          >
            Please accept the terms and conditions to proceed
          </Snack>
        )}
      </Snackbar>
    </Modal>
  );
};

export default TAC;
